import { BaseLayout, ContainedProductRow } from '../../layouts';
import { IncomingMessage, ServerResponse } from 'http';
import { retrieveProduct, retrieveRelatedProducts } from '../../api/products';

import Cookies from 'cookies';
import { ProductDetail } from '../../components/ProductDetail';
import { ProductRow } from '../../shared/ProductsRow';
import type { ProductType } from '../../types/product';
import { TopMainBanner } from '../../shared/TopMainBanner';
import { getMarketplace } from '../../api/marketplace';
import { getStoreId } from '../../api/server/getStoreId';
import { initialFetch } from '../../api/server/initialFetch';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { state } from '../../types/store/state';
import type { staticProps } from '../../types/pages/props';
import { uiStateMap } from '../../utils/uiStateMap';
import type { uiType } from '../../types/ui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';

interface Props {
  product: ProductType;
  relatedProducts: Array<ProductType>;
}

const Product = (props: Props) => {
  const { t } = useTranslation();
  const metatags = useSelector((state: state) => state.ui.metatags);
  const domain = useSelector((state: state) => state.ui.domain);
  const storeName = useSelector((state: state) => state.ui.name);

  const breadcrumbs = [
    {
      name: 'Home',
      url: '/',
    },
    {
      name: props.product.product.name,
      url: `/product/${props.product.id}/`,
    },
  ];

  return (
    <BaseLayout
      {...metatags}
      domain={domain}
      storeName={storeName}
      title={`${storeName} | ${t('product.page.title')} ${
        props.product.product.name
      }`}
    >
      <TopMainBanner isCompact={true} title="" breadcrumbs={breadcrumbs} />
      <ContainedProductRow>
        <ProductDetail {...props.product} />
        <ProductRow
          title={t('product.detail.related.products.title')}
          elements={props.relatedProducts}
        />
      </ContainedProductRow>
    </BaseLayout>
  );
};

export const getServerSideProps = async (context: staticProps) => {
  try {
    const translations = await serverSideTranslations(context.locale);
    const marketplace = await getMarketplace(context.req.headers.host);
    const cookies = new Cookies(context.req as unknown as IncomingMessage, context.res as ServerResponse);
    const sessionCookie = cookies.get('user_token');

    if (!marketplace.data.length) {
      return {
        props: {
          ...translations,
        },
        notFound: true,
      };
    }

    const marketplaceMap: uiType = uiStateMap(marketplace);
    const branchStoreId = await getStoreId(sessionCookie, marketplaceMap.storeId, marketplaceMap.businessId);

    if (branchStoreId === 0) {
      return {
        notFound: true,
      };
    }

    const response = await initialFetch(branchStoreId);

    let product;
    let relatedProducts = [];

    try {
      product = await retrieveProduct({
        id: marketplaceMap.storeId,
        productId: context.params.id,
      });
    } catch (error: any) {
      if (error.response.status === 404) {
        return {
          notFound: true,
        };
      }
      throw new Error(error);
    }

    try {
      const related = await retrieveRelatedProducts({
        id: marketplaceMap.storeId,
        productId: context.params.id,
      });

      if (related?.results?.length) {
        relatedProducts = related.results;
      } else {
        relatedProducts = [];
      }
    } catch (error: any) {
      if (error.response.status === 404) {
        return {
          notFound: true,
        };
      }
      throw new Error(error);
    }

    const preloadedState = JSON.parse(
      JSON.stringify({
        ui: marketplaceMap,
        categories: response.categories.results,
        branchStore: response.branchStoreDetail,
        branchStoreSchedule: response.branchStoreSchedule.results,
      })
    );

    return {
      props: {
        ...translations,
        preloadedState: preloadedState,
        product,
        relatedProducts,
      },
    };
  } catch (error: any) {
    throw new Error(error);
  }
};

export default Product;
